<template>
  <div class="custom-calendar">
    <AIVHorizontalScroll :sticky-offset="104">
    <div class="calendar-week-view team-view">
      <div class="week-time-header">
        <div class="date-label team-name-header">Caregiver</div>
        <div
          class="time-slot-header"
          v-for="(time, idx) in timeSlots"
          :key="idx"
        >
          {{ time }}
        </div>
      </div>

      <div v-if="caregivers.length === 0" class="team-empty-state">
        <i class="fas fa-users"></i>
        <span>No caregivers to display. Use filters to select caregivers.</span>
      </div>

      <div
        class="calendar-row"
        v-for="caregiver in caregivers"
        :key="caregiver.id"
        :style="{ height: `${calculateRowHeight(caregiver)}px` }"
      >
        <div class="date-label team-name-label" :title="caregiver.name">
          {{ caregiver.name }}
        </div>

        <div class="time-slots-container">
          <div
            v-if="isToday"
            class="current-time-line"
            :style="lineStyle"
          ></div>
          <!-- Time Off Indicator -->
          <div v-if="getCaregiverTimeOff(caregiver.id)" class="time-off-bar" :style="computeTimeOffStyle(getCaregiverTimeOff(caregiver.id))">
            <i class="fas fa-umbrella-beach"></i>
            <span>{{ formatTimeOffDisplay(getCaregiverTimeOff(caregiver.id)) }}</span>
          </div>
          <el-tooltip
            v-for="shift in shiftsForCaregiver(caregiver.id)"
            :key="shift.id"
            placement="bottom"
            effect="light"
            :open-delay="300"
            :disabled="shift.isAvailability"
          >
            <div slot="content" v-html="formatShiftTooltipHtml(shift)"></div>
            <div
              class="shift-block"
              :class="[
                shift.statusClass,
                getPayerClass(shift),
                getShiftTypeClass(shift),
                { 'availability-slot': shift.isAvailability }
              ]"
              :style="computeEventStyle(shift, caregiver.id)"
              @click.stop="shift.isAvailability ? null : $emit('show-details', shift)"
            >
              <template v-if="shift.isAvailability">
                <i class="fas fa-clock"></i>
                <span class="availability-time">{{ shift.formattedStartTime }} - {{ shift.formattedEndTime }}</span>
              </template>
              <template v-else>
                <span class="shift-time">
                  {{ shift.formattedStartTime }}
                </span>
                <span class="shift-info">{{ shift.groupName || shift.client }}</span>
              </template>
            </div>
          </el-tooltip>
        </div>
      </div>

      <!-- Unassigned shifts row -->
      <div
        v-if="unassignedShifts.length > 0"
        class="calendar-row unassigned-row"
        :style="{ height: `${calculateUnassignedRowHeight()}px` }"
      >
        <div class="date-label team-name-label unassigned-label" title="Unassigned">
          <i class="fas fa-exclamation-triangle"></i>
          Unassigned
        </div>
        <div class="time-slots-container">
          <div
            v-if="isToday"
            class="current-time-line"
            :style="lineStyle"
          ></div>
          <el-tooltip
            v-for="shift in unassignedShifts"
            :key="shift.id"
            placement="bottom"
            effect="light"
            :open-delay="300"
          >
            <div slot="content" v-html="formatShiftTooltipHtml(shift)"></div>
            <div
              class="shift-block"
              :class="[
                shift.statusClass,
                getPayerClass(shift),
                getShiftTypeClass(shift),
              ]"
              :style="computeUnassignedStyle(shift)"
              @click.stop="$emit('show-details', shift)"
            >
              <span class="shift-time">{{ shift.formattedStartTime }}</span>
              <span class="shift-info">{{ shift.groupName || shift.client }}</span>
            </div>
          </el-tooltip>
        </div>
      </div>
    </div>
    </AIVHorizontalScroll>
  </div>
</template>

<script>
import AIVHorizontalScroll from "@/components/AIVHorizontalScroll.vue";
import { isDSPDGroupShift, getCaregiverShiftStatus, getStatusIcon, formatCaregiverStatusHtml, caregiverNameWithRole } from '@/utils/shiftStatus';

export default {
  name: "TeamView",
  components: { AIVHorizontalScroll },
  props: {
    caregivers: { type: Array, required: true },
    timeSlots: { type: Array, required: true },
    shifts: { type: Array, required: true },
    isToday: { type: Boolean, default: false },
    lineStyle: { type: Object, default: () => ({}) },
    userRole: { type: String, default: "Admin" },
    caregiverTimeOffs: { type: Object, default: () => ({}) },
    currentDate: { type: Date, default: () => new Date() },
  },
  methods: {
    shiftsForCaregiver(caregiverId) {
      return this.shifts.filter((shift) => {
        if (shift.isAvailability) {
          // Availability slots belong to the caregiver they were fetched for
          return shift.id && shift.id.toString().includes(`available_${caregiverId}_`);
        }
        // Check if caregiver is assigned to this shift
        if (shift.originalData && shift.originalData.caregivers) {
          return shift.originalData.caregivers.some(
            (c) => Number(c.id) === Number(caregiverId)
          );
        }
        if (shift.caregivers && shift.caregivers.length > 0) {
          return shift.caregivers.some(
            (c) => Number(c.id) === Number(caregiverId)
          );
        }
        return false;
      });
    },
    calculateRowHeight(caregiver) {
      const shifts = this.shiftsForCaregiver(caregiver.id);
      const timeOff = this.getCaregiverTimeOff(caregiver.id);
      const hasFullDayTimeOff = timeOff && timeOff.request_type !== 'single_partial_day';
      const hasAvailability = shifts.some((s) => s.isAvailability);

      if (shifts.length === 0 && timeOff) return 60;
      if (shifts.length === 0) return 60;

      const regularShifts = shifts.filter((s) => !s.isAvailability);
      const dateShifts = regularShifts.map((s) => {
        const [sh, sm] = s.startTime.split(":").map(Number);
        const [eh, em] = s.endTime.split(":").map(Number);
        return { ...s, _start: sh * 60 + sm, _end: eh * 60 + em };
      });

      const lanes = [];
      dateShifts.forEach((s) => {
        let i = lanes.findIndex((lane) =>
          lane.every((x) => x._end <= s._start)
        );
        if (i === -1) {
          lanes.push([]);
          i = lanes.length - 1;
        }
        lanes[i].push(s);
      });

      let extraLanes = 0;
      if (hasFullDayTimeOff) extraLanes += 1;
      if (hasAvailability || (timeOff && timeOff.request_type === 'single_partial_day')) extraLanes += 1;

      const numLanes = lanes.length + extraLanes;
      return Math.max(60, 30 + numLanes * 28);
    },
    calculateUnassignedRowHeight() {
      const shifts = this.unassignedShifts;
      if (shifts.length === 0) return 60;

      const dateShifts = shifts.map((s) => {
        const [sh, sm] = s.startTime.split(":").map(Number);
        const [eh, em] = s.endTime.split(":").map(Number);
        return { ...s, _start: sh * 60 + sm, _end: eh * 60 + em };
      });

      const lanes = [];
      dateShifts.forEach((s) => {
        let i = lanes.findIndex((lane) =>
          lane.every((x) => x._end <= s._start)
        );
        if (i === -1) {
          lanes.push([]);
          i = lanes.length - 1;
        }
        lanes[i].push(s);
      });

      return Math.max(60, 30 + lanes.length * 28);
    },
    computeEventStyle(shift, caregiverId) {
      const [sh, sm] = shift.startTime.split(":").map(Number);
      const [eh, em] = shift.endTime.split(":").map(Number);

      const caregiverShifts = this.shiftsForCaregiver(caregiverId);
      const hasAvailability = caregiverShifts.some((s) => s.isAvailability);
      const timeOff = this.getCaregiverTimeOff(caregiverId);
      const hasFullDayTimeOff = timeOff && timeOff.request_type !== 'single_partial_day';

      const lanes = [];
      let laneIndex = 0;

      if (shift.isAvailability) {
        laneIndex = 0;
      } else {
        const regularShifts = caregiverShifts.filter((s) => !s.isAvailability);
        const dateShifts = regularShifts.map((s) => {
          const [oh, om] = s.startTime.split(":").map(Number);
          const [oh2, om2] = s.endTime.split(":").map(Number);
          return { ...s, _start: oh * 60 + om, _end: oh2 * 60 + om2 };
        });

        dateShifts.forEach((s) => {
          let i = lanes.findIndex((lane) =>
            lane.every((x) => x._end <= s._start)
          );
          if (i === -1) {
            lanes.push([]);
            i = lanes.length - 1;
          }
          lanes[i].push(s);
          if (s.id === shift.id) laneIndex = i;
        });
      }

      let laneOffset = hasFullDayTimeOff ? 1 : 0;
      if (hasAvailability && !shift.isAvailability) {
        laneOffset += 1;
      } else if (timeOff && timeOff.request_type === 'single_partial_day' && !shift.isAvailability) {
        laneOffset += 1;
      }

      const totalSlots = 17;
      const startSlotIndex = sh - 6;
      const endSlotIndex = eh - 6;
      const startPosition = startSlotIndex + sm / 60;
      const endPosition = endSlotIndex + em / 60;
      const startPercent = Math.max((startPosition / totalSlots) * 100, 0);
      const endPercent = Math.min((endPosition / totalSlots) * 100, 100);
      const widthPercent = Math.max(endPercent - startPercent, 1);

      return {
        left: `${startPercent}%`,
        width: `${widthPercent}%`,
        top: `${(laneIndex + laneOffset) * 28}px`,
        height: "24px",
        position: "absolute",
        color: "#fff",
        fontSize: "0.75rem",
        borderRadius: "4px",
      };
    },
    computeUnassignedStyle(shift) {
      const [sh, sm] = shift.startTime.split(":").map(Number);
      const [eh, em] = shift.endTime.split(":").map(Number);

      const allUnassigned = this.unassignedShifts;
      const dateShifts = allUnassigned.map((s) => {
        const [oh, om] = s.startTime.split(":").map(Number);
        const [oh2, om2] = s.endTime.split(":").map(Number);
        return { ...s, _start: oh * 60 + om, _end: oh2 * 60 + om2 };
      });

      const lanes = [];
      let laneIndex = 0;
      dateShifts.forEach((s) => {
        let i = lanes.findIndex((lane) =>
          lane.every((x) => x._end <= s._start)
        );
        if (i === -1) {
          lanes.push([]);
          i = lanes.length - 1;
        }
        lanes[i].push(s);
        if (s.id === shift.id) laneIndex = i;
      });

      const totalSlots = 17;
      const startSlotIndex = sh - 6;
      const endSlotIndex = eh - 6;
      const startPosition = startSlotIndex + sm / 60;
      const endPosition = endSlotIndex + em / 60;
      const startPercent = Math.max((startPosition / totalSlots) * 100, 0);
      const endPercent = Math.min((endPosition / totalSlots) * 100, 100);
      const widthPercent = Math.max(endPercent - startPercent, 1);

      return {
        left: `${startPercent}%`,
        width: `${widthPercent}%`,
        top: `${laneIndex * 28}px`,
        height: "24px",
        position: "absolute",
        color: "#fff",
        fontSize: "0.75rem",
        borderRadius: "4px",
      };
    },
    getCaregiverTimeOff(caregiverId) {
      const timeOffs = this.caregiverTimeOffs[caregiverId];
      if (!timeOffs || !Array.isArray(timeOffs) || timeOffs.length === 0) return null;
      // Format the displayed date as YYYY-MM-DD for comparison
      const d = this.currentDate;
      const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      // Find time off that overlaps the displayed date
      return timeOffs.find(t => dateStr >= t.start_date && dateStr <= t.end_date) || null;
    },
    getPayerClass(shift) {
      if (this.userRole === "Caregiver") return "";
      const payer = shift.payer || 'DSPD';
      return `payer-${payer.toLowerCase().replace(/\s+/g, '-')}`;
    },
    getShiftTypeClass(shift) {
      const shiftType = shift.shiftType || '1-on-1';
      return `shift-type-${shiftType.toLowerCase().replace(/\s+/g, '-')}`;
    },
    formatShiftTooltipHtml(shift) {
      let lines = [];
      const isGroup = isDSPDGroupShift(shift.originalData) && (this.userRole === 'Admin' || this.userRole === 'Lead');

      const calcHours = (start, end) => {
        const sParts = start.split(':').map(Number);
        const eParts = end.split(':').map(Number);
        let sMin = sParts[0] * 60 + sParts[1];
        let eMin = eParts[0] * 60 + eParts[1];
        if (eMin < sMin) eMin += 24 * 60;
        const total = eMin - sMin;
        return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
      };

      if (!isGroup && (shift.hasClockIn || shift.hasClockOut)) {
        const displayStart = shift.formattedStartTime;
        const displayEnd = shift.formattedEndTime;
        if (shift.hasClockOut) {
          lines.push(`Actual: ${displayStart} - ${displayEnd}`);
          const actualStart = shift.actualStartTime || shift.formattedStartTime;
          const actualEnd = shift.actualEndTime || shift.formattedEndTime;
          if (actualStart && actualEnd) {
            lines.push(`Hours Worked: ${calcHours(actualStart, actualEnd)}`);
          }
        } else {
          lines.push(`Actual: ${displayStart} - In Progress`);
        }
        lines.push(`Scheduled: ${shift.formattedScheduledStartTime} - ${shift.formattedScheduledEndTime}`);
        lines.push(`Hours Scheduled: ${calcHours(shift.scheduledStartTime, shift.scheduledEndTime)}`);
      } else {
        const scheduledStart = isGroup ? shift.formattedScheduledStartTime || shift.formattedStartTime : shift.formattedStartTime;
        const scheduledEnd = isGroup ? shift.formattedScheduledEndTime || shift.formattedEndTime : shift.formattedEndTime;
        const startCalc = isGroup ? shift.scheduledStartTime || shift.startTime : shift.startTime;
        const endCalc = isGroup ? shift.scheduledEndTime || shift.endTime : shift.endTime;
        lines.push(`Scheduled: ${scheduledStart} - ${scheduledEnd}`);
        lines.push(`Hours Scheduled: ${calcHours(startCalc, endCalc)}`);
      }

      const clientDisplay = shift.clients && shift.clients.length > 1
        ? shift.clients.map(c => c.name).join(', ') : shift.client;
      const clientLabel = shift.clients && shift.clients.length > 1 ? 'Clients' : 'Client';

      if (shift.groupName) lines.push(`Group: ${shift.groupName}`);
      lines.push(`${clientLabel}: ${clientDisplay}`);

      const caregiverLabel = shift.caregivers && shift.caregivers.length > 1 ? 'Caregivers' : 'Caregiver';
      if (isDSPDGroupShift(shift.originalData) && (this.userRole === 'Admin' || this.userRole === 'Lead')) {
        const caregiverHtml = formatCaregiverStatusHtml(shift.originalData, shift.caregivers);
        lines.push(`${caregiverLabel}:<br>${caregiverHtml}`);
      } else {
        const caregiverDisplay = shift.caregivers && shift.caregivers.length > 1
          ? shift.caregivers.map(c => caregiverNameWithRole(c)).join(', ') : shift.caregiver;
        lines.push(`${caregiverLabel}: ${caregiverDisplay}`);
      }

      lines.push(`Payer: ${shift.payer}`);
      const serviceCodes = shift.originalData?.service_codes || [];
      const nonServiceCodes = shift.originalData?.non_service_codes || [];
      const codesToDisplay = (shift.payer === 'Non Service' ? nonServiceCodes : serviceCodes);
      const serviceCodeList = codesToDisplay.map(sc => sc.code).filter(Boolean).join(', ');
      if (serviceCodeList) lines.push(`Code: ${serviceCodeList}`);
      lines.push(`Shift Type: ${shift.shiftType}`);
      const capitalizedStatus = shift.status ? shift.status.charAt(0).toUpperCase() + shift.status.slice(1) : '';
      lines.push(`Status: ${capitalizedStatus}`);
      if (shift.miles != null) lines.push(`Miles: ${shift.miles.toFixed(1)}`);

      const cancelReason = shift.originalData?.cancel_reason;
      if (cancelReason) {
        lines.push(`<span style="color:rgb(180, 140, 100);">Canceled: ${cancelReason}</span>`);
      }

      const notes = shift.notes || shift.originalData?.shift_notes;
      if (notes) {
        lines.push(`Notes: ${notes.replace(/\n/g, '<br>')}`);
      }

      return lines.join('<br>');
    },
    formatTimeOffDisplay(timeOff) {
      if (!timeOff) return '';
      if (timeOff.request_type === 'single_partial_day' && timeOff.start_time && timeOff.end_time) {
        const startTime = this.formatTime(timeOff.start_time);
        const endTime = this.formatTime(timeOff.end_time);
        return `${startTime} - ${endTime}`;
      }
      return 'Full Day Off';
    },
    formatTime(timeStr) {
      if (!timeStr) return '';
      const parts = timeStr.split(':');
      let hours = parseInt(parts[0]);
      const minutes = parts[1] || '00';
      const ampm = hours >= 12 ? 'pm' : 'am';
      hours = hours % 12 || 12;
      return `${hours}:${minutes}${ampm}`;
    },
    computeTimeOffStyle(timeOff) {
      if (!timeOff) return {};
      if (timeOff.request_type !== 'single_partial_day') {
        return { left: '0', right: '0', width: 'auto' };
      }
      const totalSlots = 17;
      const [startHour, startMin] = timeOff.start_time.split(':').map(Number);
      const [endHour, endMin] = timeOff.end_time.split(':').map(Number);
      const startPosition = (startHour - 6) + startMin / 60;
      const endPosition = (endHour - 6) + endMin / 60;
      const startPercent = Math.max((startPosition / totalSlots) * 100, 0);
      const endPercent = Math.min((endPosition / totalSlots) * 100, 100);
      const widthPercent = Math.max(endPercent - startPercent, 1);
      return { left: `${startPercent}%`, width: `${widthPercent}%`, right: 'auto' };
    },
  },
  computed: {
    unassignedShifts() {
      return this.shifts.filter((shift) => {
        if (shift.isAvailability) return false;
        if (shift.status === 'unassigned') return true;
        const hasCaregivers = shift.originalData && shift.originalData.caregivers && shift.originalData.caregivers.length > 0;
        const hasCaregiverList = shift.caregivers && shift.caregivers.length > 0;
        return !hasCaregivers && !hasCaregiverList && shift.caregiver === 'Unassigned';
      });
    },
  },
};
</script>

<style scoped>
.calendar-week-view.team-view {
  position: relative;
  display: flex;
  flex-direction: column;
}

.team-view .week-time-header {
  display: flex;
  font-weight: bold;
  background: transparent;
  border-bottom: 2px solid #555;
}

.team-view .team-name-header {
  width: 140px;
  min-width: 140px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 0.8rem;
  color: #aaa;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.team-view .calendar-row {
  display: flex;
  border-top: 1px solid #444;
  transition: background-color 0.15s ease;
}

.team-view .calendar-row:hover {
  background-color: rgba(255, 255, 255, 0.03);
}

.team-view .team-name-label {
  width: 140px;
  min-width: 140px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  padding: 0 8px;
  font-weight: 600;
  font-size: 0.8rem;
  color: #e0e0e0;
  background: transparent;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  border-right: 1px solid #444;
}

.team-view .unassigned-label {
  color: #dc3545;
  gap: 6px;
}

.team-view .unassigned-label i {
  font-size: 0.7rem;
}

.team-view .unassigned-row {
  border-top: 2px solid #555;
  margin-top: 4px;
}

.team-view .time-slot-header,
.team-view .time-slots-container {
  position: relative;
  display: flex;
  width: 100%;
  overflow: hidden;
}

.team-view .time-slot-header {
  font-size: 0.7rem;
  color: #aaa;
}

.team-view .current-time-line {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  background-color: red;
  z-index: 5;
  pointer-events: none;
}

.team-empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 40px 20px;
  color: #888;
  font-size: 0.9rem;
}

.team-empty-state i {
  font-size: 1.2rem;
}

/* Shift block styles */
.shift-block {
  position: absolute;
  height: 24px;
  color: #fff;
  font-size: 0.75rem;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 8px;
  white-space: nowrap;
  overflow: hidden;
  z-index: 2;
  cursor: pointer;
  max-width: 100%;
  box-sizing: border-box;
}

.shift-time {
  font-weight: bold;
}

.shift-info {
  overflow: hidden;
  text-overflow: ellipsis;
  opacity: 0.9;
}

/*noinspection CssUnusedSymbol */
.status-unassigned {
  background: linear-gradient(135deg, rgba(32, 201, 151, 0.7), rgba(32, 201, 151, 0.5));
}

/*noinspection CssUnusedSymbol */
.status-scheduled {
  background: linear-gradient(135deg, rgba(52, 144, 220, 0.7), rgba(52, 144, 220, 0.5));
}

/*noinspection CssUnusedSymbol */
.status-clocked-in {
  background: linear-gradient(135deg, rgba(255, 193, 7, 0.9), rgba(255, 193, 7, 0.7));
}

/*noinspection CssUnusedSymbol */
.status-clocked-out {
  background: linear-gradient(135deg, rgba(237, 137, 54, 0.7), rgba(237, 137, 54, 0.5));
}

/*noinspection CssUnusedSymbol */
.status-approved {
  background: linear-gradient(135deg, rgba(156, 39, 176, 0.7), rgba(156, 39, 176, 0.5));
}

/*noinspection CssUnusedSymbol */
.status-available {
  background: rgba(64, 158, 255, 0.1);
  border: 1px solid rgba(64, 158, 255, 0.3);
  color: #6ba6cd;
}

.availability-slot {
  cursor: default !important;
  pointer-events: none;
}

.availability-slot i {
  font-size: 0.65rem;
  opacity: 0.8;
  color: #6ba6cd;
}

.availability-time {
  font-size: 0.7rem;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #6ba6cd;
}

/*noinspection CssUnusedSymbol */
.status-missed {
  background: linear-gradient(135deg, rgba(108, 117, 125, 0.8), rgba(108, 117, 125, 0.6));
}

/*noinspection CssUnusedSymbol */
.status-error {
  background: linear-gradient(135deg, rgba(220, 53, 69, 0.8), rgba(220, 53, 69, 0.6)); /* Red for error shifts */
}

/* Time Off Bar */
.time-off-bar {
  position: absolute;
  top: 0;
  height: 24px;
  background: rgba(255, 152, 0, 0.2);
  border: 1px solid rgba(255, 152, 0, 0.6);
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 8px;
  font-size: 0.7rem;
  color: #f57c00;
  font-weight: 600;
  z-index: 1;
  pointer-events: none;
}

.time-off-bar i {
  font-size: 0.65rem;
  opacity: 0.9;
}

.time-off-bar span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Mobile: the 17-column time grid can't fit on a phone — let it scroll
   sideways inside its own container, keeping the caregiver column pinned. */
@media (max-width: 768px) {
  .calendar-week-view.team-view {
    min-width: 780px;
  }

  .team-view .team-name-header,
  .team-view .team-name-label {
    position: sticky;
    left: 0;
    z-index: 6;
    width: 104px;
    min-width: 104px;
    background: rgba(37, 55, 65, 0.97);
    box-shadow: 2px 0 4px rgba(0, 0, 0, 0.2);
  }

  .team-view .time-slot-header {
    font-size: 0.65rem;
  }
}
</style>
